import { useAuth } from "./AuthContext";

/** Cognito group whose members may use the admin console. Mirrors the server. */
export const ADMIN_GROUP = "admins";

/**
 * Decodes the `cognito:groups` claim from the current ID token's payload.
 *
 * This is a CLIENT-SIDE convenience only — it gates what the UI offers (the nav
 * link, the admin routes), never what the API permits. The token is not
 * verified here (no signature check); every `/admin/**` endpoint independently
 * re-checks the group server-side, so a tampered token buys nothing but a
 * blank screen. We read the claim rather than calling an API so the gate is
 * synchronous and adds no request.
 */
function decodeGroups(idToken: string | undefined): string[] {
  if (!idToken) return [];
  const payload = idToken.split(".")[1];
  if (!payload) return [];
  try {
    // base64url → JSON. atob handles standard base64; swap the url-safe chars.
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const claims = JSON.parse(json) as { "cognito:groups"?: unknown };
    const groups = claims["cognito:groups"];
    if (Array.isArray(groups)) return groups.map((g) => String(g));
    if (typeof groups === "string" && groups !== "") return [groups];
    return [];
  } catch {
    return [];
  }
}

/** Whether the signed-in user is in the admin group (per their ID token). */
export function useIsAdmin(): boolean {
  const { tokens } = useAuth();
  return decodeGroups(tokens?.idToken).includes(ADMIN_GROUP);
}
